import React from 'react';
import { DataGrid } from 'devextreme-react';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { Paper, withStyles } from '@material-ui/core';
import styles from './LineCommitments.styles';
import { ILinesProperties } from './LineCommitments.properties';
import { createDataSource } from 'components/common/DataGridDevEx/DataGrid.hooks';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';
import { ActionBarIcons, columns, LineCommitmentHeaderFilterHeight, LineCommitmentHeaderFilterWidth, quantityFilter } from './LineCommitments.constants';
import { useRetrievePurchaseOrdersLineCommitments, useRetrievePurchaseOrdersLineCommitmentsSummary, useUpdateLineCommitments } from 'api/purchaseOrders/purchaseOrders';
import SummaryPanel from 'components/common/SummaryPanel';
import SummaryTableSchema from './LineCommitments.summaryTable.schema';
import { setActionBarFn } from '../OrderDetails/OrderDetails.properties';
import { usePOContextSelector } from '../PurchaseOrders.context';
import UserIdScreenDialog from 'components/common/UserIdScreenDialog';
import { isNull } from 'utils/utils';
import { isScreenRequest } from 'api/utils';

const setActionBarButtons = (setActionBarCallBack: setActionBarFn): void => { setActionBarCallBack(ActionBarIcons); };

const LineCommitments = (props: ILinesProperties) => {
    const { classes } = props;
    const { setActionBar } = React.useContext(ActionBarContext);
    const [userIdScreenDialogOpen, setUserIdScreenDialogOpen] = React.useState(false);
    const [inLineEditdata, setInLineEditdata] = React.useState(null);

    const params = new URLSearchParams(location.search);
    const PurchaseOrderId = usePOContextSelector<'PurchaseOrder'>((state) => state.PurchaseOrder) || params.get('PurchaseOrder');
    const LineNumber = usePOContextSelector<'LineNumber'>((state) => state.LineNumber) || params.get('LineNumber');

    const fetchLinesMutation = useRetrievePurchaseOrdersLineCommitments();
    const { data: PurchaseOrderCommitLineSummary, refetch: refetchPurchaseOrderCommitLineSummary } = useRetrievePurchaseOrdersLineCommitmentsSummary(PurchaseOrderId, Number(LineNumber));
    const updateLineMutation = useUpdateLineCommitments();

    const [dialogTitle, setDialogTitle] = React.useState<string>();
    const [dialogMessage, setDialogMessage] = React.useState<string>();

    const dataGridInnerRef = React.useRef<DataGrid>();

    const fetchLines = React.useCallback(
        async (BatchPage, BatchSize, Sort, _Filter) => {
            if (PurchaseOrderId !== undefined && PurchaseOrderId !== null && PurchaseOrderId !== '' && PurchaseOrderId.toString() !== 'NaN') {
                try {
                    const lines = await fetchLinesMutation.mutateAsync({
                        PurchaseOrder: PurchaseOrderId || '',
                        LineNumber: Number(LineNumber) || 0,
                        urlQueryParams: {
                            BatchPage,
                            BatchSize,
                            Sort,
                            FilterByUncommitted: quantityFilter(dataGridInnerRef?.current?.instance?.getCombinedFilter()?.flat()),
                        }
                    });

                    lines.PurchaseOrderOustandingDocuments = lines.PurchaseOrderOustandingDocuments?.map((lineCommitment, index) => ({
                        ...lineCommitment,
                        LineCommitmentId: index + 1
                    }));

                    return lines?.PurchaseOrderOustandingDocuments;
                } catch (error) {
                    console.error('Error fetching lines:', error);

                    return null;
                }
            } else {
                return null;
            }
        },
        [PurchaseOrderId, dataGridInnerRef?.current]
    );

    const linesDataSource = React.useMemo(
        () => createDataSource(
            'LineCommitmentId',
            { fetch: fetchLines, update: updatePurchaseOrderLine },
        ),
        [fetchLines]
    );

    const refreshDataGrid = React.useCallback(
        async () => {
            await linesDataSource?.reload();
            dataGridInnerRef.current?.instance?.refresh();
            dataGridInnerRef.current?.instance?.cancelEditData();
            refetchPurchaseOrderCommitLineSummary();
        },
        [linesDataSource, refetchPurchaseOrderCommitLineSummary]
    );

    async function updatePurchaseOrderLine(data: any): Promise<void> {
        const response = await updateLineMutation.mutateAsync({
            PurchaseOrder: PurchaseOrderId || '',
            LineNumber: Number(LineNumber),
            ...data,
            CommitQuantity: data.CommittedQuantity,
        });
        if (!response.Status && isScreenRequest(response.Forms) && response.Forms?.[0]?.FormId === 'UserID') {

            setDialogTitle(response.Forms?.[0]?.Title);
            setDialogMessage(response.Forms?.[0]?.Message);
            setUserIdScreenDialogOpen(true);
        } else if (response?.Status && isNull(response?.Forms)) {
            refreshDataGrid();
        } else {
            dataGridInnerRef.current.instance.cancelEditData();
        }
    }

    const handleOnSaving = (e) => {
        e.cancel = true;
        const rowData = (e.changes && e.changes.length) ? e.changes[0].data : null;
        if (rowData) {
            setInLineEditdata(rowData);
            linesDataSource.store().update(rowData.LineCommitmentId, rowData);
        } else {
            dataGridInnerRef.current.instance.cancelEditData();
        }
    };

    React.useEffect(
        () => { setActionBarButtons(setActionBar); },
        []
    );

    const submitUserIdData = React.useCallback(
        async (data: any) => {
            const response = await updateLineMutation.mutateAsync({
                PurchaseOrder: PurchaseOrderId || '',
                LineNumber: Number(LineNumber),
                ...data,
                ...inLineEditdata,
                CommitQuantity: inLineEditdata.CommittedQuantity,
            });

            if (response?.Status && isNull(response?.Forms)) {
                setUserIdScreenDialogOpen(false);
                setInLineEditdata(null);
                refreshDataGrid();
            }

            return response;
        },
        [updateLineMutation.data]
    );

    return (
        <div className={classes.parentContainer}>
            <Paper className={classes.gridContainer}>
                <DynamicDataGrid
                    dataGridClassName={classes.grid}
                    columns={columns}
                    allowHeaderFilting={true}
                    allowUpdating={true}
                    allowDelete={false}
                    calculateFieldValue={(e) => new Promise((resolve) => resolve({ e, status: true }))}
                    gridProps={{
                        dataSource: linesDataSource,
                        dataGridRef: dataGridInnerRef,
                        sorting: { mode: 'single' },
                        disableDrillDown: false,
                        disableEditing: false,
                        disableDelete: false,
                        onSaving: handleOnSaving,
                        headerFilterHeight: LineCommitmentHeaderFilterHeight,
                        headerFilterWidth: LineCommitmentHeaderFilterWidth
                    }}
                />
            </Paper>
            <div className={classes.summaryContainer}>
                <SummaryPanel
                    loading={false}
                    data={PurchaseOrderCommitLineSummary}
                    schema={SummaryTableSchema}
                    customClassNames={classes.SummaryPanelHeight}
                />
            </div>
            <UserIdScreenDialog
                open={userIdScreenDialogOpen}
                title={dialogTitle ?? 'Commit Confirmation'}
                message={dialogMessage ?? 'Are you sure you want to commit to this document?'}
                loading={false}
                onOk={(data) => submitUserIdData({ OverrideUserId: data.UserId, OverrideUserPIN: data.Pin })}
                onCancel={() => {
                    setUserIdScreenDialogOpen(false);
                    setInLineEditdata(null);
                }}
            />
        </div>
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(LineCommitments)));
